import React from 'react'
import { Assignee } from 'components/atoms/icons'
import PropTypes from 'prop-types'
import { LightningFilledIcon, RequirementsIcon } from 'transfix-icons'
import { stopDate, stopTime, apptClass, displayAutoApptStatus } from 'utils/appointmentHelpers'
import { capitalize } from 'utils/capitalize'
import { getFacilityLabels } from 'utils/facilityHelpers'
import { normalizeUrl } from 'utils/urlHelpers'
import { DISPLAY } from 'constants/display'
import { STOP_STATUS } from 'constants/stops'
import { SCHEDULER_NAME } from '../../constants/shipments'

export const LoadDetailsRow = React.memo(({ nextStopMiles, stop }) => {
  const { facility, appointment, type, stopSequence, info = {} } = stop
  const { city, companyName, detail, shipperFacilityUuid, stateCode, zipcode } = facility
  const {
    appointmentStatus,
    endAt,
    showScheduleByWebLink,
    startAt,
    startAtTime,
    startAtTimezone,
    timezone,
    updatedOrConfirmedMessage,
    updatedOrConfirmedName,
  } = appointment
  const { billOfLadingReference, poReference } = info
  const schedulingUrl = detail?.schedulingUrl || ''
  const appointmentTime = stopTime(startAt, endAt, timezone, false, startAtTime)
  const appointmentDate = stopDate(startAt, timezone)
  const appointmentStatusClass = appointmentTime ? apptClass(appointmentStatus) : ''
  const isPredictedAppt = appointmentStatus === STOP_STATUS.PREDICTED
  const { requirements, uuid } = detail || {}
  const facilityRequirements = getFacilityLabels({ ...requirements })
  const stopType = `${type.charAt(0).toUpperCase()}${stopSequence}`
  const tagClasses = ['tag', 'tag-round', 'tag-medium']
  if (type === 'delivery') tagClasses.push('tag-inverse')
  else tagClasses.push('tag-outline')

  const [, apptText] = displayAutoApptStatus(
    appointment.appointmentStatus,
    appointment.autoAppointmentSchedule,
    appointment.facilityHasContacts,
    appointment.canAutoEmail,
    appointment.appointmentTimeUpdatedByName
  )

  return (
    <div className='pv2'>
      <div className='row collapse flex-l'>
        <div className='col-md-2 col-lg-1 tc'>
          <span className={tagClasses.join(' ')}>{stopType}</span>
        </div>
        <div className='col-md-10 col-lg-5'>
          <p className='b'>
            {capitalize(city)}, {stateCode} <span className='text-secondary'>{zipcode}</span>
          </p>
          <p className='flex items-center'>
            <span className='mr1'>{appointmentDate && `${appointmentDate} `}</span>
            {isPredictedAppt && <LightningFilledIcon color='warning' />}
            <span className={appointmentStatusClass}>
              {appointmentTime ? `${appointmentTime} ${startAtTimezone}` : 'TBD'}
            </span>
          </p>
          {updatedOrConfirmedName && (
            <span className='flex-l items-center'>
              {updatedOrConfirmedName === SCHEDULER_NAME && (
                <div className='pa2'>
                  <Assignee />
                </div>
              )}
              <p>{updatedOrConfirmedMessage}</p>
            </span>
          )}
          {showScheduleByWebLink && (
            <div className='pv4'>
              <a href={normalizeUrl(schedulingUrl)} target='_blank' rel='noopener noreferrer'>
                Schedule by web / TMS
              </a>
            </div>
          )}
          {apptText && <div className='pv4'>{apptText}</div>}
          {nextStopMiles && <p>{nextStopMiles} mi</p>}
        </div>
        <div className='col-md-10 col-lg-5'>
          {uuid && (
            <p className='ellipsis'>
              <a
                href={`${__ROOT__}/facilities/${uuid}/shipper_facilities/${shipperFacilityUuid}`}
                target='_blank'
                rel='noopener noreferrer'>
                {companyName}
              </a>
            </p>
          )}
          {facilityRequirements && (
            <span>
              <RequirementsIcon />
              {facilityRequirements}
            </span>
          )}
          <p className='text-secondary'>PO: {poReference || DISPLAY.empty}</p>
          <p className='text-secondary'>BOL: {billOfLadingReference || DISPLAY.empty}</p>
        </div>
      </div>
    </div>
  )
})
LoadDetailsRow.propTypes = {
  stop: PropTypes.shape({}),
  nextStopMiles: PropTypes.string,
}

LoadDetailsRow.defaultProps = {
  stop: PropTypes.shape({}),
  nextStopMiles: '',
}
